import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, type Briefing } from '../api';
import { Chip, EmptyState, ErrorState, Loading, PageIntro, useAsync } from '../components/ui';
import { editionLabel, slotRank } from '../editions';
import { formatLocalDay, formatTime } from '../format';

export const DATE_PARAM = 'datum';

/**
 * Past editions, by day and in slot order. Each link opens the edition exactly as
 * it was published; old morning/evening editions and the seven daily slots sit
 * side by side. A date can be chosen directly.
 */
export function ArchivePage() {
  const [params, setParams] = useSearchParams();
  const date = params.get(DATE_PARAM) ?? '';
  const valid = date === '' || /^\d{4}-\d{2}-\d{2}$/.test(date);
  const { data, error, loading, reload } = useAsync(
    () => api.briefingHistory(date ? 50 : 120, 0, valid && date ? date : undefined),
    [date, valid],
  );
  const days = useAsync(() => api.editionDay(), []);
  const known = days.data?.dates ?? [];

  const byDate = new Map<string, Briefing[]>();
  for (const b of data?.briefings ?? []) {
    const list = byDate.get(b.edition_date) ?? [];
    list.push(b);
    byDate.set(b.edition_date, list);
  }

  const choose = (d: string) => {
    const next = new URLSearchParams(params);
    if (d) next.set(DATE_PARAM, d);
    else next.delete(DATE_PARAM);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageIntro eyebrow="Dein Rückblick" title="Jede Ausgabe bleibt erhalten.">
        Frühere Ausgaben zeigen genau die Kartenversionen, die damals veröffentlicht wurden. Spätere Aktualisierungen verändern keine
        veröffentlichte Ausgabe; in den Details einer Meldung siehst du, ob es eine neuere Version gibt.
      </PageIntro>
      <div className="archive-picker">
        <label htmlFor="archive-date">Tag wählen</label>
        <input
          id="archive-date"
          type="date"
          value={valid ? date : ''}
          max={known[0]}
          min={known[known.length - 1]}
          onChange={(e) => choose(e.target.value)}
        />
        {date && (
          <button type="button" className="text-button" onClick={() => choose('')}>
            Alle Tage
          </button>
        )}
      </div>
      {!valid && <ErrorState error={new Error('Das Datum ist ungültig.')} onRetry={() => choose('')} />}
      {loading && !data && <Loading />}
      {error && <ErrorState error={error} onRetry={reload} />}
      {data && byDate.size === 0 && (
        <EmptyState title={date ? `Keine Ausgabe am ${formatLocalDay(date)}.` : 'Noch keine Ausgaben.'}>
          <p className="muted">
            {date
              ? 'An diesem Tag wurde keine Ausgabe veröffentlicht.'
              : 'Ausgaben erscheinen täglich um 06:00, 09:00, 10:00, 12:00, 15:00, 18:00 und 20:00.'}
          </p>
        </EmptyState>
      )}
      {byDate.size > 0 && (
        <ol className="archive-list">
          {[...byDate.entries()].map(([day, editions]) => (
            <li key={day} className="archive-day">
              <h2 className="archive-date">{formatLocalDay(day)}</h2>
              <ul className="archive-editions">
                {[...editions]
                  .sort((a, b) => slotRank(a.edition_type) - slotRank(b.edition_type) || (a.published_at ?? '').localeCompare(b.published_at ?? ''))
                  .map((b) => (
                    <li key={b.id}>
                      <Link className="edition-link" to={`/ausgaben/${b.id}`}>
                        <strong>{editionLabel(b.edition_type)}</strong>
                        <span className="muted small">
                          {b.published_at ? `veröffentlicht ${formatTime(b.published_at)}` : 'nicht veröffentlicht'}
                          {b.revision > 1 ? ` · Revision ${b.revision}` : ''}
                        </span>
                      </Link>
                      {b.completeness?.quiet ? <Chip kind="neutral">ruhig</Chip> : null}
                      {b.completeness?.partial ? <Chip kind="warn">unvollständig</Chip> : null}
                      {b.completeness?.degraded_cards ? <Chip kind="warn">{b.completeness.degraded_cards} ohne Modell</Chip> : null}
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}
